import { keyframes } from "@emotion/react";
import { Box, Link, Typography } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import { useMemo, type JSX } from "react";

import { XOP_CONTACT, XOP_HQ } from "../lib/company.js";
import { OSM_TILE_REFERRER_POLICY, OSM_TILE_SIZE, osmPlaceUrl, tilesAround } from "../lib/osmTiles.js";

export const HQ_MAP_ZOOM = 16;

const halo = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0.75; }
  100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
`;

const overlay = {
  position: "absolute",
  bgcolor: "rgba(255,255,255,0.95)",
  color: "#1e2a44",
  borderRadius: 1,
  px: 1.25,
  py: 0.75,
  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
} as const;

/**
 * XOP Networks' headquarters on a map, with a pulsing dot on the building.
 *
 * Free OpenStreetMap tiles as plain `<img>`s (see `lib/osmTiles.ts`), so it
 * costs no library, no key and no third-party script. 5 × 3 tiles cover a
 * container up to ~1000 × 500px with the dot centred. In the dark theme the
 * tiles are inverted and hue-rotated so the map is dark too; the dot is a
 * sibling of the tile layer, so it keeps the brand colour.
 *
 * The attribution is required by OpenStreetMap's tile policy — keep it. So
 * is a Referer: each tile overrides the page's `no-referrer` with
 * `OSM_TILE_REFERRER_POLICY`, or OSM answers every tile "Access blocked".
 * Mirrors `xop-edge-customer/apps/portal-web/src/components/HeadquartersMap.tsx`.
 */
export function HeadquartersMap({ sx }: { sx?: SxProps<Theme> }): JSX.Element {
  const tiles = useMemo(() => tilesAround(XOP_HQ.lat, XOP_HQ.lon, HQ_MAP_ZOOM, 2, 1), []);

  return (
    <Box
      data-testid="hq-map"
      role="img"
      aria-label={`Map of ${XOP_CONTACT.company} headquarters, ${XOP_CONTACT.addressLine}`}
      sx={[
        { position: "relative", overflow: "hidden", minHeight: 260, bgcolor: "action.hover", isolation: "isolate" },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          filter: (t) => (t.palette.mode === "dark" ? "invert(1) hue-rotate(180deg) brightness(0.9) contrast(0.9)" : "none"),
        }}
      >
        {tiles.map((t) => (
          <Box
            key={t.key}
            component="img"
            src={t.url}
            alt=""
            referrerPolicy={OSM_TILE_REFERRER_POLICY}
            width={OSM_TILE_SIZE}
            height={OSM_TILE_SIZE}
            draggable={false}
            sx={{ position: "absolute", left: t.left, top: t.top, width: OSM_TILE_SIZE, height: OSM_TILE_SIZE, maxWidth: "none", userSelect: "none" }}
          />
        ))}
      </Box>

      {/* The dot: a solid core with a white ring over a halo that pulses
          outward (still under prefers-reduced-motion). */}
      <Box
        aria-hidden
        data-testid="hq-map-dot"
        sx={{ position: "absolute", left: "50%", top: "50%", width: 16, height: 16, transform: "translate(-50%, -50%)" }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: 16,
            height: 16,
            // Centred without the animation too — a frozen or reduced-motion
            // frame must not leave the halo offset from the dot.
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.45),
            animation: `${halo} 1.8s ease-out infinite`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0 },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            bgcolor: "primary.main",
            border: "3px solid #fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
          }}
        />
      </Box>

      <Box sx={{ ...overlay, left: 12, top: 12 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: "inherit" }}>{XOP_CONTACT.company}</Typography>
        <Typography sx={{ fontSize: 11, lineHeight: 1.3, color: "#5b6478" }}>Dallas, Texas</Typography>
      </Box>

      <Link
        href={osmPlaceUrl(XOP_HQ.lat, XOP_HQ.lon, HQ_MAP_ZOOM)}
        target="_blank"
        rel="noreferrer"
        sx={{ ...overlay, right: 12, top: 12, fontSize: 12, fontWeight: 600, color: "#2f5fb8", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
      >
        Open larger map
      </Link>

      <Box sx={{ position: "absolute", right: 0, bottom: 0, px: 0.75, py: 0.25, fontSize: 10, color: "#333", bgcolor: "rgba(255,255,255,0.85)" }}>
        ©{" "}
        <Link href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" sx={{ color: "inherit" }}>
          OpenStreetMap
        </Link>{" "}
        contributors
      </Box>
    </Box>
  );
}
